export const i18n = {
  en: {
    nav: {
      overview: "Overview",
      sovereignty: "Local Sovereignty",
      endpoints: "Endpoints",
      sync: "Sync Engine",
      database: "Database",
      operations: "Operations",
      troubleshooting: "Troubleshooting",
    },
    common: {
      close: "Close docs",
      loading: "Loading documentation…",
      admin: "admin",
      allUsers: "all users",
      method: "Method",
      path: "Path",
      description: "Description",
      auth: "Auth",
      type: "Type",
      asset: "Asset",
      filenamePattern: "Filename pattern",
      table: "Table",
      tables: "Tables",
      generatedAt: "Generated",
    },
    overview: {
      title: "Overview",
      architecture: "Architecture",
      whatLabel: "What is it?",
      howLabel: "How does it work?",
      forWhomLabel: "Who is it for?",
      liveStats: "Current state",
      statPhotos: "Photos on disk",
      statPdfs: "PDFs on disk",
      statProducts: "Batches in catalog",
      statWithPhotos: "Batches with photo URL",
      statWithPdf: "Batches with germination PDF",
      statLocalPdf: "PDFs served from local",
      statLastSync: "Last sync run",
      statNoSync: "No sync runs recorded yet",
      footer: (app) => `This documentation describes the production system at ${app}. Use the sidebar to navigate. Admin role is required to view this page.`,
    },
    sovereignty: {
      title: "Local Sovereignty",
      storage: "Storage Paths",
      naming: "Naming Convention",
      behavior: "Behavior",
      fallback: "Fallback chain",
      photos: "Photos",
      pdfs: "PDFs",
      plantPhoto: "Plant photo",
      seedPhoto: "Seed photo",
      germinationPdf: "Germination PDF",
      downloadFlow: "Download flow",
      flowHow: "How does it run?",
      flowWhatHappens: "What happens each sync?",
      flowDownloadsAll: "Does it download everything?",
      flowDownloadsAllNo: "NO. It only downloads what is missing. Files already on disk are skipped (statSync().size > 0) so we don't waste bandwidth or time.",
      flowUpdatesValues: "Does the sync update values?",
      flowUpdatesValuesYes: "YES. Each sync refreshes the values in the products table from the Alpha JSON: THC, stock (Europe, US_Canada), cycle, hermaphroditism %, photo URLs, etc. If Alpha changes a value, the next sync reflects it in the platform.",
      flowRedownloads: "Does it re-download existing files?",
      flowRedownloadsNo: "NO. archiveAsset does statSync() and if the file size > 0 bytes it's considered already downloaded and is skipped. To force re-download you must delete the file from disk.",
      flowInterval: "Current interval",
      flowManualTrigger: "Manual trigger",
      flowManualTriggerText: "SYNC button on the /sync page or POST /sync/run (admin)",
      photosFallback: (codeProxy, codePath, codePattern, codeImg) => `${codeProxy} → looks for ${codePath}${codePattern} → falls back to Google Drive URL stored in ${codeImg} / products.Seed_Image.`,
      pdfsFallback: (pdfProxy, codeLocal, codeDrive) => `PDFs: ${pdfProxy} → serves the local archived PDF if present; returns 404 otherwise (UI falls back to the original Drive link).`,
    },
    endpoints: {
      title: "API Endpoints",
      groups: {
        auth: "Auth",
        users: "Users",
        catalog: "Catalog",
        sync_admin: "Sync (admin)",
        assets: "Assets",
        docs: "Docs",
      },
    },
    sync: {
      title: "Sync Engine",
      source: "Source",
      intervalDefault: "Interval (default)",
      retentionDefault: "Retention (default)",
      bootDelay: "Boot delay",
      flow: "Flow per run",
      triggers: "Triggers",
      retentionExplainer: "About retention (important)",
      retentionDoes: "What it does:",
      retentionDoesText: "Deletes only old rows from sync_logs (sync run history) older than N days.",
      retentionDoesNot: "What it does NOT do:",
      retentionDoesNotText: "It does not delete products, photos on disk, PDFs on disk, users, or orders.",
      retentionWhere: "When it runs:",
      retentionWhereText: "At the end of each successful runSync().",
      retentionSql: "SQL executed:",
      triggersList: (interval, codePut, codePost) => [
        `Scheduler: every ${interval} min (configurable via ${codePut})`,
        `Manual: ${codePost} from the SYNC page`,
        "Boot: first run 10s after API starts",
      ],
    },
    database: {
      title: "Database",
      database: "Database",
      host: "Host",
      socket: "Socket",
    },
    operations: {
      title: "Operations",
      restartApi: "Restart API",
      manualSync: "Trigger manual sync",
      checkStorage: "Check storage",
    },
    troubleshooting: {
      title: "Troubleshooting",
    },
  },
  es: {
    nav: {
      overview: "Resumen",
      sovereignty: "Soberanía local",
      endpoints: "Endpoints",
      sync: "Motor de Sync",
      database: "Base de datos",
      operations: "Operaciones",
      troubleshooting: "Solución de problemas",
    },
    common: {
      close: "Cerrar docs",
      loading: "Cargando documentación…",
      admin: "admin",
      allUsers: "todos los usuarios",
      method: "Método",
      path: "Ruta",
      description: "Descripción",
      auth: "Auth",
      type: "Tipo",
      asset: "Recurso",
      filenamePattern: "Patrón de nombre",
      table: "Tabla",
      tables: "Tablas",
      generatedAt: "Generado",
    },
    overview: {
      title: "Resumen",
      architecture: "Arquitectura",
      whatLabel: "¿Qué es?",
      howLabel: "¿Cómo funciona?",
      forWhomLabel: "¿Para quién?",
      liveStats: "Estado actual",
      statPhotos: "Fotos en disco",
      statPdfs: "PDFs en disco",
      statProducts: "Lotes en catálogo",
      statWithPhotos: "Lotes con URL de foto",
      statWithPdf: "Lotes con PDF de germinación",
      statLocalPdf: "PDFs servidos desde local",
      statLastSync: "Última corrida de sync",
      statNoSync: "Aún no hay corridas registradas",
      footer: (app) => `Esta documentación describe el sistema en producción en ${app}. Usa el menú lateral para navegar. Solo visible para administradores.`,
    },
    sovereignty: {
      title: "Soberanía local",
      storage: "Rutas de almacenamiento",
      naming: "Convenio de nombres",
      behavior: "Comportamiento",
      fallback: "Cadena de fallback",
      photos: "Fotos",
      pdfs: "PDFs",
      plantPhoto: "Foto de planta",
      seedPhoto: "Foto de semilla",
      germinationPdf: "PDF de germinación",
      downloadFlow: "Flujo de descarga",
      flowHow: "¿Cómo corre?",
      flowWhatHappens: "¿Qué pasa en cada sync?",
      flowDownloadsAll: "¿Descarga todo?",
      flowDownloadsAllNo: "NO. Solo descarga lo que falta. Los archivos que ya están en disco se saltan (statSync().size > 0) para no desperdiciar ancho de banda ni tiempo.",
      flowUpdatesValues: "¿El sync actualiza valores?",
      flowUpdatesValuesYes: "SÍ. Cada sync refresca los valores en la tabla products desde el JSON de Alpha: THC, stock (Europe, US_Canada), ciclo, porcentaje de hermafroditismo, URLs de fotos, etc. Si Alpha cambia un valor, en el siguiente sync lo ves actualizado en la plataforma.",
      flowRedownloads: "¿Re-descarga archivos existentes?",
      flowRedownloadsNo: "NO. archiveAsset hace statSync() y si el archivo pesa > 0 bytes lo considera ya descargado y lo salta. Para forzar re-descarga hay que borrar el archivo del disco.",
      flowInterval: "Intervalo actual",
      flowManualTrigger: "Disparo manual",
      flowManualTriggerText: "Botón SYNC en la página /sync o POST /sync/run (admin)",
      photosFallback: (codeProxy, codePath, codePattern, codeImg) => `${codeProxy} → busca ${codePath}${codePattern} → si no existe, recurre a la URL de Google Drive guardada en ${codeImg} / products.Seed_Image.`,
      pdfsFallback: (pdfProxy, codeLocal, codeDrive) => `PDFs: ${pdfProxy} → sirve el PDF archivado local si existe; devuelve 404 si no (la UI cae al enlace original de Drive).`,
    },
    endpoints: {
      title: "Endpoints de la API",
      groups: {
        auth: "Auth",
        users: "Usuarios",
        catalog: "Catálogo",
        sync_admin: "Sync (admin)",
        assets: "Recursos",
        docs: "Docs",
      },
    },
    sync: {
      title: "Motor de Sync",
      source: "Origen",
      intervalDefault: "Intervalo (por defecto)",
      retentionDefault: "Retención (por defecto)",
      bootDelay: "Retardo al arrancar",
      flow: "Flujo por ejecución",
      triggers: "Disparadores",
      retentionExplainer: "Sobre la retención (importante)",
      retentionDoes: "Qué hace:",
      retentionDoesText: "Borra únicamente filas de la tabla sync_logs (historial de corridas) más viejas que N días.",
      retentionDoesNot: "Qué NO hace:",
      retentionDoesNotText: "No borra productos, no borra fotos de /storage/photos/, no borra PDFs de /storage/pdfs/, no borra usuarios ni pedidos.",
      retentionWhere: "Dónde corre:",
      retentionWhereText: "Al final de cada runSync() exitoso.",
      retentionSql: "SQL ejecutado:",
      triggersList: (interval, codePut, codePost) => [
        `Scheduler: cada ${interval} min (configurable vía ${codePut})`,
        `Manual: ${codePost} desde la página SYNC`,
        "Arranque: primera ejecución 10s después de iniciar la API",
      ],
    },
    database: {
      title: "Base de datos",
      database: "Base de datos",
      host: "Host",
      socket: "Socket",
    },
    operations: {
      title: "Operaciones",
      restartApi: "Reiniciar la API",
      manualSync: "Lanzar sync manual",
      checkStorage: "Verificar almacenamiento",
    },
    troubleshooting: {
      title: "Troubleshooting",
      retentionHeader: "Log retention (not data retention)",
      retentionKey: "retention_days (current)",
      retentionDefault: "Important: only purges old rows from sync_logs. It does NOT delete products, photos, PDFs, users, or orders. Local catalog and storage sovereignty are always preserved.",
    },
  },
};

export const LANG_KEY = "fa-docs-lang";